/* eslint-disable @typescript-eslint/no-explicit-any  */
/* eslint-disable no-useless-escape */

import { AxiosError } from 'axios';
import i18next from 'i18next';
import Cookies from 'js-cookie';

import { Http, HttpInstance } from '@mygpt/app';

import { KEY_MYGPT_TOKEN } from '../constant';
import { isGbaseRefreshExpiredResponse } from './gbaseError';
import { errorModal, handleGbaseRefreshExpired, loginExpired, translateError } from './requestError';
import { getWidgetToken, isWidgetPluginPath } from '../widgetToken';

/**
 * 结构化错误响应类型
 * 包含 HTTP 状态码和错误信息
 */
export interface ApiErrorResponse {
  statusCode: number;
  errorMsg: string;
  /**
   * 后端错误信封原始 detail(FastAPI `{"detail": ...}`)
   * 对象形态含 code 等字段(如 PASSWORD_POLICY_VIOLATION / MFA_CODE_INVALID),
   * 供 parseApiErrorCode 判别、调用方按 `detail.code` 分支处理
   */
  detail?: unknown;
}

/**
 * 带错误码的错误信封形态：{"detail": {"code": "...", "message": "...", ...}}
 * 参见安全策略前端对接文档 §0.1 / §6
 */
export interface ApiErrorCodeDetail {
  code: string;
  message?: string;
  /** ACCOUNT_LOCKED：锁定截止时间（ISO8601 UTC） */
  locked_until?: string;
  /** MFA_CODE_INVALID：剩余尝试次数 */
  attempts_left?: number;
  /** MFA_RESEND_COOLDOWN：冷却剩余秒数 */
  retry_after?: number;
  /** PASSWORD_REUSED：禁止重用的最近 N 次 */
  reuse_n?: number;
  /** PASSWORD_POLICY_VIOLATION：违反的规则键列表 */
  violations?: string[];
}

/**
 * 判别错误是否为「带错误码对象」形态（detail 为含 string 类型 code 的对象）。
 * 是则返回 detail 对象，否则返回 null（字符串 / pydantic 数组等既有形态维持原处理）。
 */
export const parseApiErrorCode = (err: unknown): ApiErrorCodeDetail | null => {
  const detail = (err as ApiErrorResponse | undefined)?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const maybe = detail as { code?: unknown };
    if (typeof maybe.code === 'string') {
      return detail as ApiErrorCodeDetail;
    }
  }
  return null;
};

/** API 文档  **/
// https://api.gbase.ai/docs-swagger#/
// 本地开发优先走 /api 代理，线上走 api 地址

interface InitInstanceParams {
  baseUrl: string;
  baseHost: string;
  isAdmin?: boolean;
  adminLoginUrl?: string;
}

/**
 * CompanyId 获取函数
 * 用于在请求拦截器中动态获取当前组织ID，避免循环依赖
 */
let getCompanyIdFn: (() => string | null | undefined) | null = null;

/**
 * 注册 CompanyId 获取函数
 * 在应用初始化时调用，提供获取 companyId 的方法
 */
export const registerCompanyIdGetter = (getter: () => string | null | undefined) => {
  getCompanyIdFn = getter;
};

let instance: HttpInstance;
export let baseUrl: string;
export let baseHost: string;
export let isAdmin: boolean;
export let adminLoginUrl: string;

export const initInstance = ({
  baseHost: envBaseHost,
  baseUrl: envBaseUrl,
  isAdmin: envIsAdmin,
  adminLoginUrl: envAdminLoginUrl,
}: InitInstanceParams) => {
  baseUrl = envBaseUrl;
  baseHost = envBaseHost;
  isAdmin = Boolean(envIsAdmin);
  adminLoginUrl = envAdminLoginUrl || '';
  instance = Http.init({
    baseURL: envBaseUrl,
  });

  /** request */
  instance.interceptors.request.use((req) => {
    if (!req.headers['notNeedToken'] || req.headers['notNeedToken'] === 'false') {
      const token = headerToken();
      req.headers.Authorization = token;
      // 同时也不要携带 cookie
      req.withCredentials = false;
    }

    Object.prototype.hasOwnProperty.call(req.headers, 'notNeedToken') && delete req.headers['notNeedToken'];

    // 自动为特定接口添加 company_id 参数
    // 支持协同管理功能：在获取 robots 和 datasets 列表时自动传入当前组织的 company_id
    if (req.url && isAdmin && getCompanyIdFn) {
      const needsCompanyId = ['/robots', '/datasets', '/ais/members', '/datasets/members'].some((path) => {
        // 精确匹配：只匹配列表接口，不匹配子路径（如 /robots/:id/datasets）
        const urlWithoutParams = req.url?.split('?')[0] || '';
        return urlWithoutParams === path || urlWithoutParams.endsWith(path);
      });

      // 只对 GET 请求自动添加 company_id 到 URL params，POST 等写操作通过 body 传递
      if (needsCompanyId && req.method?.toLowerCase() === 'get') {
        try {
          const companyId = getCompanyIdFn();
          if (companyId) {
            // 确保 params 对象存在
            if (!req.params) {
              req.params = {};
            }
            // 只有当 params 中没有 company_id 时才自动添加
            if (!req.params.company_id) {
              req.params.company_id = companyId;
            }
          }
        } catch (error) {
          console.warn('[Request Interceptor] Failed to get companyId:', error);
        }
      }
    }

    return req;
  });

  /** response */
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError<any, any>): Promise<ApiErrorResponse> => {
      // 处理网络错误或无响应的情况
      if (!error.response) {
        const networkError: ApiErrorResponse = {
          statusCode: 0,
          errorMsg: i18next.t('completions.server_error'),
        };
        return Promise.reject(networkError);
      }

      const status = error.response.status;
      let errorMsg = '';

      // 检查是否设置了跳过错误提示的配置
      const skipErrorNotification = error.config?.skipErrorNotification;

      // 定义正则表达式以匹配不固定前缀后的 "/questions/" 后面跟随任意字符，但不超过这一层路径
      const localQuestionsRegex = /^(https?:\/\/[^\/]+\/[^\/]+\/)questions\/[^\/]+?$/;
      const questionsRegex = /^(https?:\/\/[^\/]+)\/questions\/[^\/]+$/;
      const excludedRegex = [localQuestionsRegex, questionsRegex];
      const url = (error.response as any)?.url || error.response?.config.url || '';
      const noRedirectURL = ['reclassify', 'tag-groups', 'tags'];
      const isTagApi = noRedirectURL.some((path) => url.includes(path));

      // 排除不需要弹窗错误的接口
      let isExcludedApi = false;
      if (excludedRegex.some((regx) => regx.test(url))) isExcludedApi = true;
      if (skipErrorNotification) isExcludedApi = true;

      if (!isExcludedApi && error && error.response) {
        const errMsg = error.response && error.response.data ? error.response.data.detail : error.response.statusText;

        if (isGbaseRefreshExpiredResponse({ status, url, detail: errMsg })) {
          handleGbaseRefreshExpired();
          return new Promise(() => {});
        }

        /**
         * 错误信息
         * https://l8jshxfw5fs.jp.larksuite.com/wiki/A6YzwCBiHif0ZVkIeZLjV8REp49
         */
        switch (Number(status)) {
          case 401:
            // 当 skipErrorNotification 为 true 时，跳过登录跳转，让调用方自行处理错误
            if (!skipErrorNotification) {
              loginExpired();
            }
            break;
          case 403:
            if (errMsg === 'free_email_domain_not_allowed') {
              errorMsg = i18next.t('errors.free_email_domain_not_allowed');
              errorModal(errorMsg);
              break;
            }

            if (isTagApi) {
              errorModal(i18next.t('tag.no_permission'));
              break;
            }

            if (!headerToken()) {
              loginExpired();
            } else {
              errorModal(i18next.t('no_auth'));
              window.location.href = '/no-access';
            }
            break;
          // 逐步优化错误处理逻辑，大部分错误不需要统一处理，因为需要根据场景设置不同的提示语。
          // case 404:
          //   errorModal(i18next.t('errors.not_found'));
          //   break;
          case 422:
            errorMsg = i18next.t('errors.has_change');
            errorModal(errorMsg);
            break;
          case 500:
            errorMsg = i18next.t('completions.server_error');
            errorModal(errorMsg);
            break;
          default: {
            console.log('error 1', errMsg);
            // detail 可能是对象(新错误码信封)或数组(pydantic),translateError 只接受字符串
            errorMsg = translateError(typeof errMsg === 'string' ? errMsg : '');
            errorModal(errorMsg);
            break;
          }
        }
        // 不上报sentry错误，但当 skipErrorNotification 为 true 时需要正常 reject
        if (
          [401, 403].includes(status) &&
          !isTagApi &&
          errMsg !== 'free_email_domain_not_allowed' &&
          !skipErrorNotification
        ) {
          return new Promise(() => {});
        }
      }

      // 如果是stream的错误另外处理
      const body = (error.response as any).body;
      if (isReadableStream(body)) {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        const { value } = await reader.read();
        const text = decoder.decode(value, { stream: true });
        let errorText = i18next.t('completions.server_error');
        if (text) {
          try {
            const rspError = typeof text === 'string' ? JSON.parse(text) : { detail: text };
            if (rspError.detail) {
              errorText = rspError.detail;
            }
          } catch (error) {
            console.error(error);
          }
          const streamError: ApiErrorResponse = {
            statusCode: status,
            errorMsg: translateError(errorText || ''),
          };
          return Promise.reject(streamError);
        }
      }

      // 对于跳过弹窗的接口，尝试从 response detail 中提取错误信息
      if (!errorMsg && error.response?.data) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail) && detail.length > 0) {
          errorMsg = translateError(detail[0].msg || '');
        } else if (typeof detail === 'string') {
          errorMsg = translateError(detail);
        }
      }

      // 构造并返回结构化错误响应
      console.log('error 2', errorMsg);
      const apiError: ApiErrorResponse = {
        statusCode: status,
        errorMsg: errorMsg || i18next.t('errors.unknown.description'),
        // 透传原始 detail,供调用方按 detail.code 分支处理(parseApiErrorCode)
        detail: error.response?.data?.detail,
      };
      return Promise.reject(apiError);
    }
  );
};

// 小部件（跨站 iframe）读不到登录 cookie，回退到小部件内登录保存的 token
export const headerToken = () => Cookies.get(KEY_MYGPT_TOKEN()) || (isWidgetPluginPath() ? getWidgetToken() : '');

const isReadableStream = (body: ReadableStream<Uint8Array> | undefined): body is ReadableStream => {
  return body instanceof ReadableStream;
};
